/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useCallback, useMemo} from 'react'
import {
    Alert,
    AlertTitle,
    CloseButton,
    extendTheme,
    ThemeProvider,
    useChakra,
    useMultiStyleConfig,
    useToast as useChakraToast
} from '@chakra-ui/react'

let cachedToastTheme = null

/**
 * Display a toast message on the screen.
 * This is a custom hook to handle showing toasts in the app, preventing duplicate toasts and to add action elements
 * to toasts when required. It supports all props supported by Chakra toast.
 *
 * @param {string} title Message text to be displayed in toast
 * @param {string} id - id provided to the toast to avoid duplicate toast ids, use it if multiple toasts are needed
 * @param {string} status Semantic state of the toast - success | error | info | warning
 * @param {node} action Optional component to be displayed in the toast (eg. Button to allow user to perform action)
 * @param {string} position The placement of the toast on screen
 * @param {number} duration The delay before the toast hides (in milliseconds)
 */
export function useToast(options) {
    const {theme} = useChakra()
    const styles = useMultiStyleConfig('Toast', options)
    // create a subtheme based on the app theme with overrides defined in Toast.theme
    const toastTheme = useMemo(() => {
        // use a global cache as extendTheme is expensive
        if (!cachedToastTheme) {
            cachedToastTheme = extendTheme(theme, styles.theme)
        }
        return cachedToastTheme
    }, [theme, styles.theme])
    // apply the container styles from the toast theme
    const toast = useChakraToast({containerStyle: styles.container, ...options})

    return useCallback(
        ({
            title,
            id,
            status,
            action,
            position = 'bottom',
            duration = 5000,
            variant = 'solid',
            isClosable = true
        }) =>
            toast({
                id,
                title,
                status,
                isClosable,
                position,
                duration,
                variant,
                render: ({onClose}) => (
                    // Apply custom theme to use inside the toast - currently the Alert, CloseButton and Link
                    // has toast specific overrides.
                    <ThemeProvider theme={toastTheme}>
                        <Alert status={status} variant={variant}>
                            {title && <AlertTitle>{title}</AlertTitle>}
                            {action}
                            {isClosable && <CloseButton onClick={onClose} />}
                        </Alert>
                    </ThemeProvider>
                )
            }),
        [toast, toastTheme]
    )
}
